import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Save, IndianRupee, AlertCircle, Check, FileText } from 'lucide-react';

const API = '/api';

export default function PaymentSchedule({ worksheetId, totalValue, isLocked = false }) {
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  const fetchSchedule = useCallback(async () => {
    if (!worksheetId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/boq-worksheets/${worksheetId}/payment-schedule`);
      setSlabs(res.data.payment_schedule || []);
      setDirty(false);
    } catch {
      // No schedule yet
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const totalPct = slabs.reduce((sum, s) => sum + (parseFloat(s.percentage) || 0), 0);
  const isValid = Math.abs(totalPct - 100) < 0.01;
  const calcAmount = (pct) => totalValue ? Math.round(totalValue * (parseFloat(pct) || 0) / 100) : 0;

  const addSlab = () => {
    const remaining = Math.max(0, 100 - totalPct);
    setSlabs([...slabs, { milestone_name: '', percentage: remaining > 0 ? remaining : 0, description: '' }]);
    setDirty(true);
  };

  const removeSlab = (idx) => {
    setSlabs(slabs.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const updateSlab = (idx, field, value) => {
    const updated = [...slabs];
    updated[idx] = { ...updated[idx], [field]: value };
    setSlabs(updated);
    setDirty(true);
  };

  const save = async () => {
    if (!isValid && slabs.length > 0) {
      toast.error(`Total must be 100%. Currently ${totalPct.toFixed(1)}%`);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/boq-worksheets/${worksheetId}/payment-schedule`, { payment_schedule: slabs });
      toast.success('Payment schedule saved');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const generateSlabInvoice = async (slab) => {
    if (!slab.id) {
      toast.error('Please save the payment schedule first');
      return;
    }
    setGeneratingInvoice(slab.id);
    try {
      const res = await axios.post(`${API}/invoices/from-boq/${worksheetId}/slab/${slab.id}`);
      toast.success(res.data.message || 'Invoice created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(null);
    }
  };

  if (loading) return <div className="text-sm text-stone-400 py-4">Loading payment schedule...</div>;

  return (
    <div className="border border-stone-200 rounded-xl bg-white" data-testid="payment-schedule-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50 rounded-t-xl">
        <div className="flex items-center gap-2">
          <IndianRupee size={16} className="text-stone-500" />
          <h3 className="text-sm font-semibold text-stone-800">Payment Schedule</h3>
          {slabs.length > 0 && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isValid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {totalPct.toFixed(1)}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isLocked && (
            <Button variant="outline" size="sm" onClick={addSlab} className="h-7 text-xs" data-testid="add-payment-slab">
              <Plus size={14} className="mr-1" /> Add Milestone
            </Button>
          )}
          {dirty && (
            <Button size="sm" onClick={save} disabled={saving || (!isValid && slabs.length > 0)} className="h-7 text-xs bg-stone-900 text-white hover:bg-stone-800" data-testid="save-payment-schedule">
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      {slabs.length === 0 ? (
        <div className="text-center py-6 text-sm text-stone-400">
          No payment milestones configured. Click "Add Milestone" to define the payment structure.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payment-schedule-table">
            <thead>
              <tr className="border-b border-stone-100 text-stone-500 text-xs">
                <th className="text-left px-4 py-2 font-medium w-[240px]">Milestone</th>
                <th className="text-center px-2 py-2 font-medium w-[80px]">%</th>
                <th className="text-right px-4 py-2 font-medium w-[140px]">Amount</th>
                <th className="text-left px-4 py-2 font-medium">Description</th>
                <th className="text-center px-2 py-2 font-medium w-[80px]">Invoice</th>
                {!isLocked && <th className="w-[40px]"></th>}
              </tr>
            </thead>
            <tbody>
              {slabs.map((slab, idx) => (
                <tr key={idx} className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors" data-testid={`payment-slab-row-${idx}`}>
                  <td className="px-4 py-2">
                    <Input
                      value={slab.milestone_name || ''}
                      onChange={(e) => updateSlab(idx, 'milestone_name', e.target.value)}
                      placeholder="e.g. Advance"
                      className="h-8 text-sm border-stone-200"
                      disabled={isLocked}
                      data-testid={`slab-name-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={slab.percentage || ''}
                      onChange={(e) => updateSlab(idx, 'percentage', parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm text-center border-stone-200"
                      disabled={isLocked}
                      data-testid={`slab-pct-${idx}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-stone-700">
                    Rs.{calcAmount(slab.percentage).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={slab.description || ''}
                      onChange={(e) => updateSlab(idx, 'description', e.target.value)}
                      placeholder="Optional description"
                      className="h-8 text-sm border-stone-200"
                      disabled={isLocked}
                      data-testid={`slab-desc-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    {slab.id && !dirty && (
                      <button
                        onClick={() => generateSlabInvoice(slab)}
                        disabled={generatingInvoice === slab.id}
                        className="p-1.5 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        title={`Generate invoice for ${slab.milestone_name || 'this milestone'}`}
                        data-testid={`generate-invoice-slab-${idx}`}
                      >
                        <FileText size={14} />
                      </button>
                    )}
                  </td>
                  {!isLocked && (
                    <td className="px-2 py-2">
                      <button onClick={() => removeSlab(idx)} className="p-1 text-stone-400 hover:text-red-500 transition-colors" data-testid={`remove-slab-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-50">
                <td className="px-4 py-2 font-semibold text-stone-700 text-sm">Total</td>
                <td className={`px-2 py-2 text-center font-bold text-sm ${isValid ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {totalPct.toFixed(1)}%
                  {isValid && <Check size={12} className="inline ml-1" />}
                </td>
                <td className="px-4 py-2 text-right font-bold text-stone-800 text-sm">
                  Rs.{(totalValue || 0).toLocaleString()}
                </td>
                <td colSpan={isLocked ? 2 : 3} className="px-4 py-2">
                  {!isValid && slabs.length > 0 && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle size={12} /> Must total 100%
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
